import { useState, useEffect, useCallback } from "react";
import { Box, Button } from "@mui/material";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import { getFormattedDate } from "../../../utils/commonFunctions";
import RefreshIcon from "@mui/icons-material/Refresh";
import UnsubscribeMembershipDialog from "./components/UnsubscribeMembershipDialog";
import CommonButton from "../../../components/CommonButton/CommonButton";
import AddMembershipDialog from "./components/AddMembershipDialog";
import CustomButton from "../../../components/CustomButton/Button";

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

interface UserMembershipsTabProps {
  userId: string;
}

interface MembershipData {
  establishmentname: string;
  DateSignUp: string;
  Notifications: number;
  SubscriptionStatus: number;
  Id: string;
}

const UserMembershipsTab = ({ userId }: UserMembershipsTabProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<{
    data: MembershipData[];
  } | null>(null);

  // State for the dialog
  const [openUnsubscribeDialog, setOpenUnsubscribeDialog] = useState(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [unsubscribeError, setUnsubscribeError] = useState("");
  const [selectedMembership, setSelectedMembership] = useState<{
    establishmentName: string;
    Id: string;
  } | null>(null);

  const [openAddDialog, setOpenAddDialog] = useState(false);

  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>(
    []
  );

  const columns = [
    {
      id: "establishmentname",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "DateSignUp",
      label: "Created At",
      minWidth: 150,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "Notifications",
      label: "Notifications",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "SubscriptionStatus",
      label: "Subscription Status",
      minWidth: 150,
      align: "left",
      format: (value: number) => (
        <Button
          variant="contained"
          size="small"
          sx={{
            backgroundColor: value === 1 ? "success.main" : "error.main",
            color: "white",
            textTransform: "none",
            minWidth: "100px",
            "&:hover": {
              backgroundColor: value === 1 ? "success.dark" : "error.dark"
            },
            cursor: "default"
          }}
        >
          {value === 1 ? "Active" : "Inactive"}
        </Button>
      )
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "left",
      format: (value: any, row: MembershipData) =>
        row.SubscriptionStatus === 1 ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CommonButton
              onClick={(e: any) => {
                e.stopPropagation();
                setSelectedMembership({
                  establishmentName: row.establishmentname,
                  Id: row.Id
                });
                setOpenUnsubscribeDialog(true);
              }}
              variant="outlined"
              color="error"
            >
              Unsubscribe
            </CommonButton>
          </Box>
        ) : (
          "-"
        )
    }
  ];

  const fetchMemberships = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${apiEndpoint}/loyaltyprogram/membership/user/${userId}`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => setMemberships(res.data))
      .catch(() => setError("Failed to fetch memberships"))
      .finally(() => setLoading(false));
  }, [userId]);

  const fetchBusinesses = useCallback(() => {
    axios
      .get(`${apiEndpoint}/business`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        // Assuming response is an array of businesses with id and name
        setBusinesses(
          res.data.map((b: any) => ({
            id: b.Id || b.id,
            name: b.establishmentname || b.name
          }))
        );
      })
      .catch(() => setBusinesses([]));
  }, []);

  useEffect(() => {
    fetchMemberships();
  }, [userId, fetchMemberships]);

  useEffect(() => {
    if (openAddDialog) fetchBusinesses();
  }, [openAddDialog, fetchBusinesses]);

  const handleUnsubscribeConfirm = async () => {
    if (!selectedMembership?.Id) {
      setUnsubscribeError(
        "Cannot unsubscribe: MerchantId is missing from the API response."
      );
      return;
    }

    setUnsubscribeError("");
    setUnsubscribeLoading(true);

    try {
      await axios.put(
        `${apiEndpoint}/loyaltyprogram/membership/unsubscribe`,
        {
          UserId: userId,
          MerchantId: selectedMembership.Id
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      handleCloseUnsubscribeDialog();
      fetchMemberships(); // Refresh data on success
    } catch (err) {
      console.error("Failed to unsubscribe:", err);
      const errorMessage =
        (err as any)?.response?.data?.message ||
        "Failed to unsubscribe from membership. Please try again.";
      setUnsubscribeError(errorMessage);
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  const handleCloseUnsubscribeDialog = () => {
    if (!unsubscribeLoading) {
      setOpenUnsubscribeDialog(false);
      setUnsubscribeError("");
      setSelectedMembership(null);
    }
  };

  return (
    <Box>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%"
          }}
        >
          <CustomButton callBack={() => setOpenAddDialog(true)}>
            Add Membership
          </CustomButton>
          <RefreshIcon onClick={fetchMemberships} />
        </div>
      </div>
      <CustomTableWithPagination
        columns={columns}
        data={memberships?.data || []}
        apiStatus={loading ? "fetching" : "idle"}
        payloadSize={100}
        currentPage={1}
        totalCount={memberships?.data?.length || 0}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No memberships found."
      />

      {selectedMembership && (
        <UnsubscribeMembershipDialog
          open={openUnsubscribeDialog}
          onClose={handleCloseUnsubscribeDialog}
          onConfirm={handleUnsubscribeConfirm}
          loading={unsubscribeLoading}
          error={unsubscribeError}
          establishmentName={selectedMembership.establishmentName}
        />
      )}
      <AddMembershipDialog
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false);
        }}
        userId={userId}
        businesses={businesses}
        onSuccess={() => {
          fetchMemberships();
          setOpenAddDialog(false);
        }}
      />
    </Box>
  );
};

export default UserMembershipsTab;
